import { useCallback, useEffect, useRef, useState } from "react";

/** How long a typed prefix stays live before the next keystroke starts a new search. */
const TYPEAHEAD_RESET_MS = 500;

export interface UseListboxNavOptions {
  itemCount: number;
  /** Text used for typeahead matching, by index. */
  getLabel: (index: number) => string;
  /** Disabled items are skipped by every movement and cannot be selected. */
  isDisabled?: (index: number) => boolean;
  onSelect: (index: number) => void;
  /** Escape. Omit for a listbox that has nothing to dismiss. */
  onDismiss?: () => void;
  /**
   * Tab and Shift+Tab, which move focus out of the list. Wire it to the popup's
   * close for a listbox that lives in one; omit for a listbox that is part of
   * the page, where Tab is an ordinary move and closes nothing.
   */
  onTabOut?: () => void;
  /** Wrap past the ends with the arrow keys. Defaults to true. */
  loop?: boolean;
}

export interface UseListboxNavResult {
  /** -1 until something is active; feed it to aria-activedescendant or per-item state. */
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  handleKeyDown: (event: React.KeyboardEvent) => void;
  resetTypeahead: () => void;
}

/**
 * Headless keyboard behaviour for the WAI-ARIA listbox/menu pattern: arrow and
 * Home/End movement that skips disabled items, Enter/Space selection, Escape
 * dismissal, prefix typeahead, and a Tab that closes a popup list on its way
 * out. Owns only the active index - rendering, roles and focus management stay
 * with the component.
 */
export function useListboxNav(
  options: UseListboxNavOptions,
): UseListboxNavResult {
  const [requestedIndex, setActiveIndex] = useState(-1);

  /*
   * An item count that shrinks under us (a filtered list) must not leave the
   * active index pointing past the end. Clamped as it is read rather than
   * corrected in an effect: an effect paints one frame with the stale index
   * before fixing it, and this is a derivation, not state. `itemCount: 0`
   * clamps to -1, which is already the hook's "nothing active".
   */
  const activeIndex = Math.min(requestedIndex, options.itemCount - 1);

  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  const queryRef = useRef("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTypeahead = useCallback(() => {
    queryRef.current = "";
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => resetTypeahead, [resetTypeahead]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const {
        itemCount,
        getLabel,
        isDisabled,
        onSelect,
        onDismiss,
        onTabOut,
        loop = true,
      } = optionsRef.current;

      const enabled = (index: number) => !isDisabled?.(index);

      /** Next enabled index from `from` walking in `direction`, or -1 if there is none. */
      const seek = (
        from: number,
        direction: 1 | -1,
        allowLoop: boolean,
      ): number => {
        if (itemCount === 0) return -1;
        let index = from;
        for (let step = 0; step < itemCount; step += 1) {
          index += direction;
          if (index < 0 || index > itemCount - 1) {
            if (!allowLoop) return -1;
            index = index < 0 ? itemCount - 1 : 0;
          }
          if (enabled(index)) return index;
        }
        return -1;
      };

      const moveTo = (next: number) => {
        if (next === -1) return;
        event.preventDefault();
        setActiveIndex(next);
      };

      switch (event.key) {
        case "ArrowDown":
          resetTypeahead();
          moveTo(seek(activeIndex, 1, loop));
          return;
        case "ArrowUp":
          resetTypeahead();
          moveTo(seek(activeIndex, -1, loop));
          return;
        case "Home":
          resetTypeahead();
          moveTo(seek(-1, 1, false));
          return;
        case "End":
          resetTypeahead();
          moveTo(seek(itemCount, -1, false));
          return;
        case "Escape":
          resetTypeahead();
          if (onDismiss) {
            event.preventDefault();
            onDismiss();
          }
          return;
        case "Tab":
          /* The APG closes the popup on Tab, and it is the only key here that
             must keep its default action: preventing it would strand focus on a
             list that is closing. So no preventDefault - the browser runs its
             own sequential navigation afterwards, from whatever `onTabOut` left
             focused. Select and Dropdown hand focus back to the trigger there,
             so Tab continues from the trigger and lands on the control after
             it, which is where the user was heading. Shift+Tab is the same key
             and the same close; only the direction differs. */
          resetTypeahead();
          onTabOut?.();
          return;
        case "Enter":
          if (
            activeIndex >= 0 &&
            activeIndex < itemCount &&
            enabled(activeIndex)
          ) {
            event.preventDefault();
            resetTypeahead();
            onSelect(activeIndex);
          }
          return;
        default:
          break;
      }

      /* Space selects, except mid-search where it is part of the typed text. */
      if (event.key === " " && queryRef.current === "") {
        if (
          activeIndex >= 0 &&
          activeIndex < itemCount &&
          enabled(activeIndex)
        ) {
          event.preventDefault();
          onSelect(activeIndex);
        }
        return;
      }

      const isPrintable =
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey;
      if (!isPrintable) return;

      event.preventDefault();
      queryRef.current += event.key.toLowerCase();
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        queryRef.current = "";
        timerRef.current = null;
      }, TYPEAHEAD_RESET_MS);

      /* Repeating one character cycles through the items starting with it, which
         is what the APG asks for and what "aaa" would otherwise break. */
      const query = queryRef.current;
      const prefix = /^(.)\1*$/.test(query) ? query.slice(0, 1) : query;

      for (let step = 1; step <= itemCount; step += 1) {
        const index =
          (Math.max(activeIndex, -1) + step + itemCount) % itemCount;
        if (!enabled(index)) continue;
        if (getLabel(index).toLowerCase().startsWith(prefix)) {
          setActiveIndex(index);
          return;
        }
      }
    },
    [activeIndex, resetTypeahead],
  );

  return { activeIndex, setActiveIndex, handleKeyDown, resetTypeahead };
}
